// Gateway between the UI and the sim/data modules. Loads the real modules dynamically so the UI
// keeps working (via src/ui/devMock.js) if they are missing or broken, or when ?mock is in the URL.
// Panels only talk to `api`, never import sim modules directly.

export const api = {
  mode: 'real',
  problems: [],          // human-readable load problems (shown in console + Log panel)
  newGame: null, tick: null,
  actions: {}, sel: {}, save: {}, data: {}, market: {}, tok: {},
  m2: {},                // src/data/m2/{rules,regions,tech,news}.js merged (optional; {} in mock mode)
  m3: {},                // src/data/m3/rules.js (optional)
};

const REAL = {
  state: '../core/state.js',
  save: '../core/save.js',
  actions: '../core/actions.js',
  tick: '../sim/tick.js',
  selectors: '../sim/selectors.js',
  market: '../economy/market.js',
  tokens: '../economy/tokens.js',
  eras: '../data/eras.js',
  fields: '../data/fields.js',
  equipment: '../data/equipment.js',
  cpi: '../data/cpi.js',
  wages: '../data/wages.js',
  news: '../data/news.js',
  achievements: '../data/achievements.js',
  regions: '../data/regions.js',
  tech: '../data/tech.js',
  refining: '../data/refining.js',
  // M2 rule tables & helpers (pure). Kept under api.m2 so their names never shadow M1 data exports.
  m2rules: '../data/m2/rules.js',
  m2regions: '../data/m2/regions.js',
  m2tech: '../data/m2/tech.js',
  m2news: '../data/m2/news.js',
  m3rules: '../data/m3/rules.js', // M3 rule tables (HEDGING, LEASE_ROUNDS, …) under api.m3
};

async function tryImport(path) {
  try { return { mod: await import(path) }; } catch (err) { return { err }; }
}

export async function loadApi({ forceMock = false } = {}) {
  const mods = {};
  const errs = {};
  // perf(load): one import of the aggregate (apiModules.js) so the production bundle loads the sim as a single
  // chunk; if any module in it fails (dev, half-written module), fall back to per-module loading below.
  let all = null;
  if (!forceMock) { try { all = await import('./apiModules.js'); } catch (err) { console.warn('[driller] apiModules.js failed, loading modules one by one:', err?.message ?? err); } }
  if (all) {
    for (const k of Object.keys(REAL)) if (all[k]) mods[k] = all[k];
    // a module added to REAL but not to apiModules.js: still load it (unbundled), and say so
    for (const k of Object.keys(REAL)) if (!all[k]) { console.warn(`[driller] ${REAL[k]} missing from src/ui/apiModules.js`); const r = await tryImport(REAL[k]); if (r.mod) mods[k] = r.mod; else errs[k] = r.err; }
  }
  else if (!forceMock) {
    // state.js first: importing sim modules in another order can trip a circular-import TDZ
    // (research.js grantFn) — see CONTRACT changelog. The rest can load in parallel.
    const first = await tryImport(REAL.state);
    const entries = [['state', first], ...await Promise.all(Object.entries(REAL).filter(([k]) => k !== 'state').map(async ([k, p]) => [k, await tryImport(p)]))];
    for (const [k, r] of entries) { if (r.mod) mods[k] = r.mod; else errs[k] = r.err; }
  }
  const realOk = !forceMock && mods.state?.newGame && mods.tick?.tick && mods.actions;
  if (realOk) {
    api.mode = 'real';
    api.newGame = mods.state.newGame;
    api.tick = mods.tick.tick;
    api.actions = { ...pickFns(mods.tokens), ...pickFns(mods.actions) };
    api.sel = { ...(mods.selectors ?? {}) };
    api.save = { ...(mods.save ?? {}) };
    api.market = { ...(mods.market ?? {}) };
    api.tok = { ...(mods.tokens ?? {}) };
    api.data = {
      ...(mods.eras ?? {}), ...(mods.cpi ?? {}), ...(mods.wages ?? {}), ...(mods.fields ?? {}),
      ...(mods.equipment ?? {}), ...(mods.news ?? {}), ...(mods.achievements ?? {}), ...(mods.regions ?? {}), ...(mods.tech ?? {}), ...(mods.refining ?? {}),
    };
    api.m3 = { ...(mods.m3rules ?? {}) };
    api.m2 = { ...(mods.m2news ?? {}), ...(mods.m2tech ?? {}), ...(mods.m2regions ?? {}), ...(mods.m2rules ?? {}) };
    for (const [k, e] of Object.entries(errs)) api.problems.push(`${REAL[k]}: ${e?.message ?? e}`);
  } else {
    const m = await import('./devMock.js');
    api.mode = 'mock';
    api.newGame = m.newGame;
    api.tick = m.tick;
    api.actions = { ...m.actions };
    api.sel = { netWorth: m.netWorth, monthlyPL: m.monthlyPL, regionView: m.regionView };
    api.save = m.save;
    api.market = { basePriceForDay: m.basePriceForDay, spotPrice: (s) => s.market.spot };
    // Prefer real data modules where they exist, falling back to the mock's.
    api.data = { FIELDS: m.FIELDS, EQUIPMENT: m.EQUIPMENT, availableEquipment: m.availableEquipment, ROLES: m.ROLES,
      ERAS: m.ERAS, eraForYear: m.eraForYear, REGIONS: m.REGIONS, regionRisk: m.regionRisk,
      ...(mods.cpi ?? {}), ...(mods.eras ?? {}) };
    if (!forceMock) {
      for (const k of ['state', 'tick', 'actions']) if (errs[k]) api.problems.push(`${REAL[k]}: ${errs[k]?.message ?? errs[k]}`);
    }
  }
  if (api.problems.length) console.warn('[driller] module problems:\n' + api.problems.join('\n'));
  console.info(`[driller] running in ${api.mode} mode`);
  return api;
}

function pickFns(mod) { const o = {}; for (const [k, v] of Object.entries(mod ?? {})) if (typeof v === 'function') o[k] = v; return o; }

// Call an action by name or function. Always returns { ok, msg }.
export function callAction(state, fnOrName, ...args) {
  const fn = typeof fnOrName === 'function' ? fnOrName : api.actions[fnOrName];
  if (!fn) return { ok: false, msg: `Not available yet (${fnOrName}).` };
  try {
    const r = fn(state, ...args);
    if (r && typeof r === 'object' && 'ok' in r) return r;
    return { ok: true, msg: '', value: r };
  } catch (err) {
    console.error(err);
    return { ok: false, msg: `Error: ${err.message}` };
  }
}
export const has = (name) => typeof api.actions[name] === 'function';
// First available action name from a list of candidates (sim APIs that may land under either name).
export const firstAction = (...names) => names.find((n) => typeof api.actions[n] === 'function') ?? null;
